import { z } from 'zod';
import { DiaEvento } from '../types/diaEvento';
import { EventInterval } from '../types/dateConfig';

/**
 * Schema de validação para criação de novos eventos
 * Baseado na interface Event do types/index.ts
 * Atualizado para incluir horários, duração livre e intervalos
 */
export const eventCreateSchema = z.object({
  nome: z
    .string()
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .max(100, 'Nome não pode ter mais de 100 caracteres')
    .trim(),
  
  dataInicio: z
    .string()
    .min(1, 'Data e horário de início são obrigatórios')
    .refine((datetime) => {
      // Verificar se é um datetime válido
      const selectedDate = new Date(datetime);
      return !isNaN(selectedDate.getTime());
    }, 'Data e horário de início inválidos')
    .refine((datetime) => {
      const selectedDate = new Date(datetime);
      const now = new Date();
      return selectedDate >= now;
    }, 'Data e horário de início devem ser no futuro'),
  
  dataFim: z
    .string()
    .min(1, 'Data e horário de fim são obrigatórios')
    .refine((datetime) => {
      const selectedDate = new Date(datetime);
      return !isNaN(selectedDate.getTime());
    }, 'Data e horário de fim inválidos'),
  
  slotDurationMin: z
    .number()
    .min(5, 'Duração mínima é 5 minutos')
    .max(240, 'Duração máxima é 240 minutos (4 horas)')
    .int('Duração deve ser um número inteiro'),
  
  intervalBetweenSlots: z
    .number()
    .min(0, 'Intervalo não pode ser negativo')
    .max(60, 'Intervalo máximo é 60 minutos')
    .int('Intervalo deve ser um número inteiro'),
  
  lunchBreak: z.object({
    enabled: z.boolean(),
    startTime: z.string().optional(),
    endTime: z.string().optional()
  }).refine((lunch) => {
    if (!lunch.enabled) return true;
    return lunch.startTime && lunch.endTime;
  }, {
    message: 'Horários de almoço são obrigatórios quando habilitado',
    path: ['startTime']
  }).refine((lunch) => {
    if (!lunch.enabled || !lunch.startTime || !lunch.endTime) return true;
    return lunch.startTime < lunch.endTime;
  }, {
    message: 'Horário de fim deve ser posterior ao início',
    path: ['endTime']
  }),

  // Nova estrutura de dias do evento (opcional para compatibilidade)
  diasEvento: z.array(z.object({
    id: z.string().min(1, 'ID do dia é obrigatório'),
    data: z.string().refine((date) => {
      return /^\d{4}-\d{2}-\d{2}$/.test(date);
    }, 'Data deve estar no formato YYYY-MM-DD'),
    inicio: z.string().refine((datetime) => {
      const date = new Date(datetime);
      return !isNaN(date.getTime());
    }, 'Horário de início deve ser um datetime válido ISO 8601'),
    fim: z.string().refine((datetime) => {
      const date = new Date(datetime);
      return !isNaN(date.getTime());
    }, 'Horário de fim deve ser um datetime válido ISO 8601'),
    intervalos: z.array(z.object({
      id: z.string().min(1, 'ID do intervalo é obrigatório'),
      inicio: z.string().refine((time) => {
        return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
      }, 'Formato de horário inválido (HH:MM)'),
      fim: z.string().refine((time) => {
        return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
      }, 'Formato de horário inválido (HH:MM)'),
      descricao: z.string().min(1, 'Descrição é obrigatória').max(100, 'Descrição muito longa'),
    }).refine((intervalo) => {
      return intervalo.inicio < intervalo.fim;
    }, {
      message: 'Horário de fim deve ser posterior ao início',
      path: ['fim']
    })).optional().default([])
    // Interval conflict validation removed - conflicts between intervals are now allowed
  }).refine((diaEvento) => {
    const inicio = new Date(diaEvento.inicio);
    const fim = new Date(diaEvento.fim);
    return inicio < fim;
  }, {
    message: 'Horário de fim deve ser posterior ao horário de início',
    path: ['fim']
  })).optional(),

  // Intervalos personalizados durante o evento (estrutura legada - mantida para compatibilidade)
  intervalos: z.array(z.object({
    id: z.string(),
    inicio: z.string().refine((time) => {
      return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
    }, 'Formato de horário inválido (HH:MM)'),
    fim: z.string().refine((time) => {
      return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
    }, 'Formato de horário inválido (HH:MM)'),
    descricao: z.string().min(1, 'Descrição é obrigatória').max(100, 'Descrição muito longa'),
  }).refine((intervalo) => {
    return intervalo.inicio < intervalo.fim;
  }, {
    message: 'Horário de fim deve ser posterior ao início',
    path: ['fim']
  })).optional().default([]).refine((intervalos) => {
    if (!intervalos || intervalos.length === 0) return true;
    // Validar se duração total dos intervalos não excede o evento
    const totalDuration = intervalos.reduce((total, intervalo) => {
      const startMin = timeToMinutes(intervalo.inicio);
      const endMin = timeToMinutes(intervalo.fim);
      return total + Math.max(0, endMin - startMin);
    }, 0);
    return totalDuration <= 8 * 60; // Máximo 8 horas de intervalos por dia
  }, 'Duração total dos intervalos excede o limite de 8 horas'),

  // Configurações de datas do sistema de tabs (para sincronização)
  dateConfigs: z.array(z.any()).optional(),

  // Campos opcionais de branding e configuração
  floorPlanUrl: z.string().url('URL do mapa deve ser válida').optional(),
  
  brand: z.object({
    logoUrl: z.string().url('URL do logo deve ser válida').optional(),
    primaryColor: z.string()
      .regex(/^#[0-9A-F]{6}$/i, 'Cor primária deve ser um código hexadecimal válido (#RRGGBB)')
      .default('#0070f3'),
    secondaryColor: z.string()
      .regex(/^#[0-9A-F]{6}$/i, 'Cor secundária deve ser um código hexadecimal válido (#RRGGBB)')
      .default('#0052cc'),
    accentColor: z.string()
      .regex(/^#[0-9A-F]{6}$/i, 'Cor de destaque deve ser um código hexadecimal válido (#RRGGBB)')
      .optional(),
    fontFamily: z.string().max(100, 'Nome da fonte muito longo').optional(),
    customCss: z.string().max(5000, 'CSS customizado muito longo').optional(),
  }).default({
    primaryColor: '#0070f3',
    secondaryColor: '#0052cc',
  }),

  // Campos de auditoria (gerados automaticamente)
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
}).refine((data) => {
  const dataInicio = new Date(data.dataInicio);
  const dataFim = new Date(data.dataFim);
  // Permitir mesmo dia, desde que horário de fim seja posterior ao início
  return dataInicio < dataFim;
}, {
  message: 'Horário de fim deve ser posterior ao horário de início',
  path: ['dataFim']
}).refine((data) => {
  const dataInicio = new Date(data.dataInicio);
  const dataFim = new Date(data.dataFim);
  const diffHours = (dataFim.getTime() - dataInicio.getTime()) / (1000 * 60 * 60);
  return diffHours >= 0.5; // Mínimo 30 minutos
}, {
  message: 'Evento deve ter duração mínima de 30 minutos',
  path: ['dataFim']
}).refine((data) => {
  // Validar se configuração gera pelo menos 1 slot usando diasEvento quando disponível
  if (!data.dataInicio || !data.dataFim || !data.slotDurationMin) return true;
  
  try {
    const lunchConfig = data.lunchBreak?.enabled ? {
      enabled: data.lunchBreak.enabled,
      startTime: data.lunchBreak.startTime,
      endTime: data.lunchBreak.endTime
    } : undefined;
    
    // Usar intervalos de diasEvento se disponível, senão usar intervalos legados
    let validIntervalos: EventInterval[] = [];
    
    if (data.diasEvento && data.diasEvento.length > 0) {
      // Compilar todos os intervalos de todos os dias
      validIntervalos = data.diasEvento.flatMap(dia => 
        dia.intervalos?.map(intervalo => ({
          id: intervalo.id,
          inicio: intervalo.inicio,
          fim: intervalo.fim,
          descricao: intervalo.descricao
        })) || []
      );
    } else {
      // Usar intervalos legados filtrados
      validIntervalos = (data.intervalos || []).filter((i): i is EventInterval => 
        !!i && !!i.id && !!i.inicio && !!i.fim && !!i.descricao
      );
    }
    
    const calculation = calculateEventSlots(
      data.dataInicio,
      data.dataFim,
      data.slotDurationMin,
      data.intervalBetweenSlots || 0,
      lunchConfig,
      validIntervalos
    );
    return calculation.totalSlots > 0;
  } catch {
    return false;
  }
}, {
  message: 'Configuração não gera nenhum slot disponível. Verifique horários e durações.',
  path: ['dataFim']
}).refine((data) => {
  // Validar consistência entre diasEvento e dataInicio/dataFim
  if (!data.diasEvento || data.diasEvento.length === 0) return true;
  
  const eventoInicio = new Date(data.dataInicio);
  const eventoFim = new Date(data.dataFim);
  
  // Verificar se todos os dias estão dentro do período do evento
  for (const dia of data.diasEvento) {
    const diaDate = new Date(dia.data + 'T00:00:00Z');
    
    // Verificar se o dia está dentro do período
    const eventoInicioDate = new Date(eventoInicio.toISOString().split('T')[0] + 'T00:00:00Z');
    const eventoFimDate = new Date(eventoFim.toISOString().split('T')[0] + 'T00:00:00Z');
    
    if (diaDate < eventoInicioDate || diaDate > eventoFimDate) {
      return false;
    }
    
    // Verificar se os horários do dia são consistentes com o evento
    const diaInicio = new Date(dia.inicio);
    const diaFim = new Date(dia.fim);
    
    // No primeiro dia, verificar se não começa antes do evento
    if (diaDate.getTime() === eventoInicioDate.getTime()) {
      if (diaInicio < eventoInicio) {
        return false;
      }
    }
    
    // No último dia, verificar se não termina depois do evento
    if (diaDate.getTime() === eventoFimDate.getTime()) {
      if (diaFim > eventoFim) {
        return false;
      }
    }
  }
  
  return true;
}, {
  message: 'Os dias do evento devem estar dentro do período e ter horários consistentes',
  path: ['diasEvento']
});

/**
 * Tipo inferido do schema para TypeScript
 */
export type EventCreateFormData = z.infer<typeof eventCreateSchema>;

/**
 * Schema específico para validação de um dia de evento
 */
export const diaEventoSchema = z.object({
  id: z.string().min(1, 'ID do dia é obrigatório'),
  data: z.string().refine((date) => {
    return /^\d{4}-\d{2}-\d{2}$/.test(date);
  }, 'Data deve estar no formato YYYY-MM-DD'),
  inicio: z.string().refine((datetime) => {
    const date = new Date(datetime);
    return !isNaN(date.getTime());
  }, 'Horário de início deve ser um datetime válido ISO 8601'),
  fim: z.string().refine((datetime) => {
    const date = new Date(datetime);
    return !isNaN(date.getTime());
  }, 'Horário de fim deve ser um datetime válido ISO 8601'),
  intervalos: z.array(z.object({
    id: z.string().min(1, 'ID do intervalo é obrigatório'),
    inicio: z.string().refine((time) => {
      return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
    }, 'Formato de horário inválido (HH:MM)'),
    fim: z.string().refine((time) => {
      return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
    }, 'Formato de horário inválido (HH:MM)'),
    descricao: z.string().min(1, 'Descrição é obrigatória').max(100, 'Descrição muito longa'),
  })).optional().default([])
}).refine((dia) => {
  const inicio = new Date(dia.inicio);
  const fim = new Date(dia.fim);
  return inicio < fim;
}, {
  message: 'Horário de fim deve ser posterior ao horário de início',
  path: ['fim']
});

/**
 * Converte horário string para minutos desde o início do dia
 * @param timeString "09:30" -> 570 minutos
 */
export const timeToMinutes = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Converte minutos para string de horário
 * @param minutes 570 -> "09:30"
 */
export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Verifica se um horário está dentro de um intervalo
 */
export const isTimeInInterval = (
  time: string, 
  intervalStart: string, 
  intervalEnd: string
): boolean => {
  const timeMin = timeToMinutes(time);
  const startMin = timeToMinutes(intervalStart);
  const endMin = timeToMinutes(intervalEnd);
  return timeMin >= startMin && timeMin < endMin;
};

/**
 * Calcula duração total dos intervalos em minutos
 */
export const calculateIntervalsDuration = (intervalos: EventInterval[]): number => {
  return intervalos.reduce((total, intervalo) => {
    const startMin = timeToMinutes(intervalo.inicio);
    const endMin = timeToMinutes(intervalo.fim);
    
    // Verificar se os valores são válidos (não NaN)
    if (isNaN(startMin) || isNaN(endMin)) {
      return total;
    }
    
    const duration = endMin - startMin;
    // Só adicionar se a duração for positiva (horário válido)
    return total + Math.max(0, duration);
  }, 0);
};

/**
 * Valida se há conflitos entre intervalos personalizados e horário de almoço
 */
export const validateEventConfiguration = (
  lunchBreak: { enabled: boolean; startTime?: string; endTime?: string; },
  intervalos: EventInterval[]
): { isValid: boolean; conflicts: string[] } => {
  // Interval validation disabled - conflicts between intervals are now allowed
  return { isValid: true, conflicts: [] };
};

/**
 * Calcula o total de slots disponíveis para o evento considerando intervalos personalizados
 */
export const calculateEventSlots = (
  dataInicio: string,
  dataFim: string,
  slotDurationMin: number,
  intervalBetweenMin: number = 0,
  lunchBreak?: { enabled: boolean; startTime?: string; endTime?: string; },
  intervalos: EventInterval[] = []
): { totalSlots: number; breakdown: Array<{date: string; slots: number; intervalosDoDia: EventInterval[]}> } => {
  const start = new Date(dataInicio);
  const end = new Date(dataFim);
  
  const breakdown: Array<{date: string; slots: number; intervalosDoDia: EventInterval[]}> = [];
  let totalSlots = 0;
  
  // Iterar por cada dia do evento
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);
  
  while (current <= end) {
    // Definir horários de trabalho do dia
    const dayStart = new Date(current);
    const dayEnd = new Date(current);
    
    // Se é o primeiro dia, usar horário de início do evento
    if (current.toDateString() === start.toDateString()) {
      dayStart.setHours(start.getHours(), start.getMinutes(), 0, 0);
    } else {
      dayStart.setHours(9, 0, 0, 0); // Padrão 9h
    }
    
    // Se é o último dia, usar horário de fim do evento
    if (current.toDateString() === end.toDateString()) {
      dayEnd.setHours(end.getHours(), end.getMinutes(), 0, 0);
    } else {
      dayEnd.setHours(18, 0, 0, 0); // Padrão 18h
    }
    
    // Calcular minutos de trabalho no dia
    let workingMinutes = (dayEnd.getTime() - dayStart.getTime()) / (1000 * 60);
    
    // Lista para armazenar intervalos que afetam este dia
    const intervalosDoDia: EventInterval[] = [];
    
    // Subtrair almoço se habilitado e dentro do horário
    if (lunchBreak?.enabled && lunchBreak.startTime && lunchBreak.endTime) {
      const lunchStart = timeToMinutes(lunchBreak.startTime);
      const lunchEnd = timeToMinutes(lunchBreak.endTime);
      const dayStartMin = dayStart.getHours() * 60 + dayStart.getMinutes();
      const dayEndMin = dayEnd.getHours() * 60 + dayEnd.getMinutes();
      
      // Verificar se há sobreposição do almoço com o horário de trabalho
      const lunchOverlapStart = Math.max(lunchStart, dayStartMin);
      const lunchOverlapEnd = Math.min(lunchEnd, dayEndMin);
      
      if (lunchOverlapStart < lunchOverlapEnd) {
        workingMinutes -= (lunchOverlapEnd - lunchOverlapStart);
      }
    }
    
    // Subtrair intervalos personalizados
    for (const intervalo of intervalos) {
      const intervalStart = timeToMinutes(intervalo.inicio);
      const intervalEnd = timeToMinutes(intervalo.fim);
      const dayStartMin = dayStart.getHours() * 60 + dayStart.getMinutes();
      const dayEndMin = dayEnd.getHours() * 60 + dayEnd.getMinutes();
      
      // Verificar se há sobreposição do intervalo com o horário de trabalho
      const intervalOverlapStart = Math.max(intervalStart, dayStartMin);
      const intervalOverlapEnd = Math.min(intervalEnd, dayEndMin);
      
      if (intervalOverlapStart < intervalOverlapEnd) {
        workingMinutes -= (intervalOverlapEnd - intervalOverlapStart);
        intervalosDoDia.push(intervalo);
      }
    }
    
    // Calcular slots do dia
    const totalSlotTime = slotDurationMin + intervalBetweenMin;
    const daySlots = Math.floor(Math.max(0, workingMinutes) / totalSlotTime);
    
    breakdown.push({
      date: current.toISOString().split('T')[0],
      slots: daySlots,
      intervalosDoDia
    });
    
    totalSlots += daySlots;
    
    // Próximo dia
    current.setDate(current.getDate() + 1);
  }
  
  return { totalSlots, breakdown };
};

/**
 * Formatar horas em formato brasileiro (ex: 9h 30min)
 */
export const formatDuration = (hours: number): string => {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  
  if (wholeHours === 0 && minutes === 0) {
    return "0min";
  } else if (wholeHours === 0) {
    return `${minutes}min`;
  } else if (minutes === 0) {
    return `${wholeHours}h`;
  } else {
    return `${wholeHours}h ${minutes}min`;
  }
};

/**
 * Formatar datetime para exibição brasileira
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Gerar horários padrão para seleção
 */
export const generateTimeOptions = (start = 6, end = 22, step = 30): Array<{value: string; label: string}> => {
  const options = [];
  for (let hour = start; hour <= end; hour++) {
    for (let minute = 0; minute < 60; minute += step) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push({
        value: timeString,
        label: timeString
      });
    }
  }
  return options;
};

/**
 * Horários padrão de almoço
 */
export const DEFAULT_LUNCH_TIMES = {
  startTime: '12:00',
  endTime: '13:00'
};

/**
 * Converte um evento com estrutura legada para a nova estrutura diasEvento
 */
export const convertTodiasEvento = (
  dataInicio: string,
  dataFim: string,
  intervalosLegados: EventInterval[] = []
): DiaEvento[] => {
  const start = new Date(dataInicio);
  const end = new Date(dataFim);
  const diasEvento: DiaEvento[] = [];
  
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);
  
  while (current <= end) {
    // Definir horários de trabalho do dia
    const dayStart = new Date(current);
    const dayEnd = new Date(current);
    
    // Se é o primeiro dia, usar horário de início do evento
    if (current.toDateString() === start.toDateString()) {
      dayStart.setHours(start.getHours(), start.getMinutes(), 0, 0);
    } else {
      dayStart.setHours(9, 0, 0, 0); // Padrão 9h
    }
    
    // Se é o último dia, usar horário de fim do evento
    if (current.toDateString() === end.toDateString()) {
      dayEnd.setHours(end.getHours(), end.getMinutes(), 0, 0);
    } else {
      dayEnd.setHours(18, 0, 0, 0); // Padrão 18h
    }
    
    const diaId = current.toISOString().split('T')[0];
    
    diasEvento.push({
      id: diaId,
      data: diaId,
      inicio: dayStart.toISOString(),
      fim: dayEnd.toISOString(),
      slotDurationMin: 30, // Padrão de 30 minutos
      slotIntervalDurationMin: 10, // Padrão de 10 minutos entre slots
      intervalos: intervalosLegados.map(intervalo => ({
        id: `${diaId}-${intervalo.id}`,
        inicio: intervalo.inicio,
        fim: intervalo.fim,
        descricao: intervalo.descricao
      }))
    });
    
    // Próximo dia
    current.setDate(current.getDate() + 1);
  }
  
  return diasEvento;
};

/**
 * Extrai todos os intervalos de todos os dias do evento
 */
export const extrairTodosIntervalos = (diasEvento: DiaEvento[]): EventInterval[] => {
  return diasEvento.flatMap(dia => 
    dia.intervalos?.map(intervalo => ({
      id: intervalo.id,
      inicio: intervalo.inicio,
      fim: intervalo.fim,
      descricao: intervalo.descricao
    })) || []
  );
};

/**
 * Valida conflitos de intervalos agrupados por dia
 * Permite intervalos com mesmos horários em dias diferentes
 */
export const validateIntervalsByDay = (
  diasEvento: Array<{
    id: string;
    data: string;
    inicio: string;
    fim: string;
    intervalos?: Array<{
      id: string;
      inicio: string;
      fim: string;
      descricao: string;
    }>;
  }>,
  lunchBreak?: { enabled: boolean; startTime?: string; endTime?: string; }
): { isValid: boolean; conflicts: string[] } => {
  // Interval validation disabled - conflicts between intervals are now allowed
  return { isValid: true, conflicts: [] };
};

/**
 * Calcula estatísticas dos dias do evento
 */
export const calcularEstatisticasDias = (diasEvento: DiaEvento[]): {
  totalDias: number;
  totalIntervalos: number;
  duracaoTotalMinutos: number;
  duracaoMediaPorDia: number;
} => {
  const totalDias = diasEvento.length;
  let totalIntervalos = 0;
  let duracaoTotalMinutos = 0;
  
  diasEvento.forEach(dia => {
    const inicioMs = new Date(dia.inicio).getTime();
    const fimMs = new Date(dia.fim).getTime();
    duracaoTotalMinutos += (fimMs - inicioMs) / (1000 * 60);
    totalIntervalos += dia.intervalos?.length || 0;
  });
  
  const duracaoMediaPorDia = totalDias > 0 ? duracaoTotalMinutos / totalDias : 0;
  
  return {
    totalDias,
    totalIntervalos,
    duracaoTotalMinutos,
    duracaoMediaPorDia
  };
};

/**
 * Valida se um conjunto de dias do evento é válido
 */
export const validarDiasEvento = (
  diasEvento: DiaEvento[],
  dataInicioEvento: string,
  dataFimEvento: string
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (diasEvento.length === 0) {
    return { isValid: true, errors: [] }; // Estrutura opcional
  }
  
  // Validar ordem cronológica
  for (let i = 0; i < diasEvento.length - 1; i++) {
    const diaAtual = new Date(diasEvento[i].data);
    const proximoDia = new Date(diasEvento[i + 1].data);
    
    if (diaAtual >= proximoDia) {
      errors.push(`Os dias devem estar em ordem cronológica: ${diasEvento[i].data} >= ${diasEvento[i + 1].data}`);
    }
  }
  
  // Validar se estão dentro do período do evento
  const eventoInicio = new Date(dataInicioEvento.split('T')[0]);
  const eventoFim = new Date(dataFimEvento.split('T')[0]);
  
  diasEvento.forEach((dia, index) => {
    const diaDate = new Date(dia.data);
    
    if (diaDate < eventoInicio || diaDate > eventoFim) {
      errors.push(`Dia ${index + 1} (${dia.data}) está fora do período do evento`);
    }
  });
  
  // Usar a nova função de validação por dia
  const intervalValidation = validateIntervalsByDay(diasEvento);
  if (!intervalValidation.isValid) {
    errors.push(...intervalValidation.conflicts);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};